import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskLines } from "./Reveal";
import { IMAGES } from "../data/content";

const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const copyY = useTransform(scrollYProgress, [0, 1], ["0%", "-26%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const onMove = (e) => {
      const nx = (e.clientX / window.innerWidth - 0.5) * 2;
      const ny = (e.clientY / window.innerHeight - 0.5) * 2;
      setTilt({ x: nx, y: ny });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="grain relative flex min-h-[80svh] items-end overflow-hidden bg-obsidian"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={IMAGES.heroNature}
          alt="Misty mountain valley at sunrise in Honduras"
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-obsidian/72" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/35 to-obsidian/80" />
      </motion.div>

      {/* floating 3D logo plate */}
      <motion.div
        className="pointer-events-none absolute right-[3%] top-[9%] z-10 hidden lg:block"
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.6, delay: 0.5, ease: EASE }}
        style={{ perspective: 1000 }}
      >
        <div
          className="float-slow"
          style={{
            transform: `rotateY(${tilt.x * 11}deg) rotateX(${-tilt.y * 9}deg)`,
            transition: "transform 420ms cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          <div className="relative">
            <div className="absolute -inset-20 rounded-full bg-gold/10 blur-[90px]" />
            <img
              src="/logo.jpg"
              alt="Three Abide Ministries — Faith, Hope, Love"
              className="relative h-[24rem] w-[24rem] object-contain opacity-95 xl:h-[27rem] xl:w-[27rem]"
              style={{
                maskImage:
                  "radial-gradient(ellipse 66% 74% at 50% 50%, #000 48%, rgba(0,0,0,0.6) 74%, transparent 100%)",
                WebkitMaskImage:
                  "radial-gradient(ellipse 66% 74% at 50% 50%, #000 48%, rgba(0,0,0,0.6) 74%, transparent 100%)",
              }}
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        style={{ y: copyY, opacity: fade }}
        className="relative z-20 mx-auto w-full max-w-[1500px] px-6 pb-16 pt-32 lg:px-12 lg:pb-20"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="label-eyebrow mb-8 flex flex-wrap items-center gap-x-4 gap-y-2 text-sand/55"
        >
          <span className="text-gold">Est. 2013</span>
          <span className="h-px w-10 bg-sand/25" />
          <span>Faith · Hope · Love</span>
          <span className="h-px w-10 bg-sand/25" />
          <span>1 Corinthians 13:13</span>
        </motion.div>

        <h1 className="font-display max-w-5xl text-[3rem] font-normal leading-[0.95] tracking-[-0.025em] text-sand sm:text-6xl lg:text-[6.2rem]">
          <MaskLines lines={["Three Abide"]} delay={0.35} />
        </h1>

        <div className="mt-10 flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.95, ease: EASE }}
            className="max-w-xl text-sm leading-relaxed text-sand/70 md:text-base"
          >
            For thirteen years, Three Abide Ministries (formerly Abba Padre International) has
            carried faith, hope, and love into the places they are hardest to find.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1, ease: EASE }}
            className="flex flex-wrap items-center gap-4"
          >
            <a
              href="#give"
              data-testid="hero-give-btn"
              className="btn-sheen inline-flex items-center gap-2 rounded-full bg-crimson px-8 py-4 font-mono-accent text-[0.7rem] uppercase tracking-[0.2em] text-sand"
            >
              Give Now <ArrowDownRight size={15} />
            </a>
            <a
              href="#about"
              data-testid="hero-explore-btn"
              className="btn-sheen inline-flex items-center gap-2 rounded-full border border-sand/30 px-8 py-4 font-mono-accent text-[0.7rem] uppercase tracking-[0.2em] text-sand hover:bg-sand hover:text-obsidian"
            >
              Our Story
            </a>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
